import { readAppConfig } from "./appConfig.js";
import { audienceBenefitSeoByPath } from "./audienceBenefits.js";
import { fallbackPublicSiteOrigin } from "./brand.js";
import { gameTheoryPage } from "./gameTheoryContent.js";
import {
  MARKET_OG_HEIGHT,
  MARKET_OG_WIDTH,
  buildDynamicMarketOgPublicUrl,
  buildMarketOgAltText
} from "./marketOgImages.js";
import { seasonZeroPage } from "./seasonZeroContent.js";
import { appendSocialCardFreshness, normalizeSocialCardFreshnessValue } from "./socialCardFreshness.js";

export { fallbackPublicSiteOrigin };

export const defaultDescription = "Launch a market, rally a crowd, and put visible weight behind the outcome you want to move.";
const defaultLogoPath = "/og-logo.png";
const defaultSocialImagePath = "/raiziq_ogimage.png";

function routeMetadataFor(serviceName) {
  return {
    "/": { title: serviceName, description: defaultDescription },
    "/markets": { title: `Markets | ${serviceName}`, description: defaultDescription },
    "/create": { title: `Create a Market | ${serviceName}`, description: defaultDescription },
    "/leaderboards": { title: `Leaderboards | ${serviceName}`, description: defaultDescription },
    "/profile": { title: `Profile | ${serviceName}`, description: defaultDescription },
    "/faq": { title: `FAQ | ${serviceName}`, description: defaultDescription },
    "/solana_app_store": {
      title: `Solana App Store Discovery | ${serviceName}`,
      description:
        "An independent RaizIQ view of apps observed from the Solana Mobile dApp Store, organized by category, rating signals, update freshness, permission labels, and public metadata."
    },
    "/privacy": { title: `Privacy Policy | ${serviceName}`, description: defaultDescription },
    "/terms": { title: `Terms | ${serviceName}`, description: defaultDescription },
    [gameTheoryPage.path]: {
      title: `${gameTheoryPage.seo.titleBase} | ${serviceName}`,
      description: gameTheoryPage.seo.description
    },
    [seasonZeroPage.path]: {
      title: `${seasonZeroPage.seo.titleBase} | ${serviceName}`,
      description: seasonZeroPage.seo.description
    },
    ...Object.fromEntries(
      Object.entries(audienceBenefitSeoByPath).map(([path, metadata]) => [
        path,
        {
          title: `${metadata.titleBase} | ${serviceName}`,
          description: metadata.description
        }
      ])
    )
  };
}

export function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function truncateMeta(value, maxLength = 160) {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
}

export function getPublicSiteOrigin(env = undefined) {
  return readAppConfig(env || getImportMetaEnv()).publicSiteOrigin;
}

function getImportMetaEnv() {
  return typeof import.meta !== "undefined" ? import.meta.env : undefined;
}

export function absoluteUrl(pathOrUrl = "/", origin = getPublicSiteOrigin()) {
  const raw = String(pathOrUrl || "/");
  if (/^https?:\/\//i.test(raw)) return raw;

  const cleanOrigin = String(origin || getPublicSiteOrigin()).replace(/\/+$/, "");
  return `${cleanOrigin}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

export function canonicalPath(value = "/") {
  const [pathOnly] = String(value || "/").split(/[?#]/);
  const safePath = pathOnly || "/";
  return safePath.startsWith("/") ? safePath : `/${safePath}`;
}

export function canonicalSlug(value = "") {
  const [slugOnly] = String(value || "").split(/[?#]/);
  return encodeURIComponent(slugOnly);
}

export function buildSiteSeo({ path = "/", origin, env } = {}) {
  const config = readAppConfig(env || getImportMetaEnv());
  const cleanPath = canonicalPath(path);
  const routeMetadata = routeMetadataFor(config.serviceName);
  const metadata = routeMetadata[cleanPath] || { title: config.serviceName, description: defaultDescription };
  const resolvedOrigin = origin || config.publicSiteOrigin;

  return {
    title: metadata.title,
    description: metadata.description,
    canonicalUrl: absoluteUrl(cleanPath, resolvedOrigin),
    imageUrl: absoluteUrl(defaultSocialImagePath, resolvedOrigin),
    logoUrl: absoluteUrl(defaultLogoPath, resolvedOrigin),
    type: "website",
    siteName: config.serviceName,
    twitterCard: "summary_large_image"
  };
}

export function buildMarketSeo({ market, origin, env, socialFreshnessValue } = {}) {
  const runtimeEnv = env || getImportMetaEnv();
  const config = readAppConfig(runtimeEnv);
  const resolvedOrigin = origin || config.publicSiteOrigin;
  const titleText = String(market?.title || "Market").trim() || "Market";
  const freshnessValue = normalizeSocialCardFreshnessValue(socialFreshnessValue);
  const options = (market?.options || [])
    .map((option) => option?.label)
    .filter(Boolean)
    .join(" vs ");
  const description = truncateMeta(
    [market?.summary, options ? `Options: ${options}.` : ""].filter(Boolean).join(" ")
  );
  const slugOrId = canonicalSlug(market?.slug || market?.id || "");
  const canonicalUrl = absoluteUrl(`/market/${slugOrId}`, resolvedOrigin);
  let socialUrl = canonicalUrl;
  let imageUrl = absoluteUrl(defaultSocialImagePath, resolvedOrigin);
  let imageAlt;
  let ogImageWidth;
  let ogImageHeight;
  let ogImageType;

  if (freshnessValue) {
    socialUrl = appendSocialCardFreshness(canonicalUrl, freshnessValue).href;
  }

  try {
    const storedOgImageUrl = normalizeHttpsImageUrl(market?.ogImageUrl || market?.og_image_url);
    let marketOgImageUrl = "";
    if (market?.id && runtimeEnv?.PM_MARKET_IMAGES_PUBLIC_ORIGIN) {
      try {
        marketOgImageUrl = buildDynamicMarketOgPublicUrl({
          env: runtimeEnv,
          schema: runtimeEnv?.SUPABASE_SCHEMA || "public",
          marketId: market.id
        });
      } catch {
        marketOgImageUrl = "";
      }
    }
    marketOgImageUrl ||= storedOgImageUrl;

    if (marketOgImageUrl) {
      imageUrl = marketOgImageUrl;
      imageAlt = buildMarketOgAltText(market, { serviceName: config.serviceName });
      ogImageWidth = MARKET_OG_WIDTH;
      ogImageHeight = MARKET_OG_HEIGHT;
      ogImageType = "image/png";
    }

  } catch {
    imageUrl = absoluteUrl(defaultSocialImagePath, resolvedOrigin);
    imageAlt = undefined;
    ogImageWidth = undefined;
    ogImageHeight = undefined;
    ogImageType = undefined;
  }

  if (freshnessValue) {
    imageUrl = appendSocialCardFreshness(imageUrl, freshnessValue).href;
  }

  return {
    title: `${titleText} | ${config.serviceName}`,
    description: description || defaultDescription,
    canonicalUrl,
    socialUrl,
    imageUrl,
    imageAlt,
    ogImageWidth,
    ogImageHeight,
    ogImageType,
    logoUrl: absoluteUrl(defaultLogoPath, resolvedOrigin),
    type: "article",
    siteName: config.serviceName,
    twitterCard: "summary_large_image"
  };
}

function normalizeHttpsImageUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const url = new URL(raw);
    return url.protocol === "https:" ? url.href : "";
  } catch {
    return "";
  }
}

export function renderSeoTags(metadata) {
  const title = escapeHtml(metadata.title);
  const description = escapeHtml(metadata.description);
  const canonicalUrl = escapeHtml(metadata.canonicalUrl);
  const socialUrl = escapeHtml(metadata.socialUrl || metadata.canonicalUrl);
  const imageUrl = escapeHtml(metadata.imageUrl);
  const logoUrl = escapeHtml(metadata.logoUrl || absoluteUrl(defaultLogoPath));
  const type = escapeHtml(metadata.type || "website");
  const card = escapeHtml(metadata.twitterCard || "summary_large_image");
  const site = escapeHtml(metadata.siteName || readAppConfig(getImportMetaEnv()).serviceName);
  const imageAlt = String(metadata.imageAlt || "").trim();
  const imageAltContent = imageAlt ? escapeHtml(imageAlt) : "";
  const ogImageWidth = Number(metadata.ogImageWidth || 0);
  const ogImageHeight = Number(metadata.ogImageHeight || 0);
  const ogImageType = String(metadata.ogImageType || "").trim();

  return [
    `<title>${title}</title>`,
    `<meta name="description" content="${description}" />`,
    `<link rel="canonical" href="${canonicalUrl}" />`,
    `<meta property="og:site_name" content="${site}" />`,
    `<meta property="og:type" content="${type}" />`,
    `<meta property="og:title" content="${title}" />`,
    `<meta property="og:description" content="${description}" />`,
    `<meta property="og:url" content="${socialUrl}" />`,
    `<meta property="og:image" content="${imageUrl}" />`,
    ...(Number.isFinite(ogImageWidth) && ogImageWidth > 0
      ? [`<meta property="og:image:width" content="${ogImageWidth}" />`]
      : []),
    ...(Number.isFinite(ogImageHeight) && ogImageHeight > 0
      ? [`<meta property="og:image:height" content="${ogImageHeight}" />`]
      : []),
    ...(ogImageType ? [`<meta property="og:image:type" content="${escapeHtml(ogImageType)}" />`] : []),
    ...(imageAltContent ? [`<meta property="og:image:alt" content="${imageAltContent}" />`] : []),
    `<meta property="og:logo" content="${logoUrl}" />`,
    `<meta name="twitter:card" content="${card}" />`,
    `<meta name="twitter:title" content="${title}" />`,
    `<meta name="twitter:description" content="${description}" />`,
    `<meta name="twitter:image" content="${imageUrl}" />`,
    ...(imageAltContent ? [`<meta name="twitter:image:alt" content="${imageAltContent}" />`] : [])
  ].join("\n    ");
}

export function escapeXml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
